import React from 'react'
import {KTCard as Card, KTCardBody as CardBody} from '../../../../../_metronic/helpers'

/**
 * Generic dashboard stat card
 *
 * Props keep styling decisions (“bi” icon, ribbon colour, text colour)
 * outside the component so one component fits many use-cases.
 */
export interface StatCardProps {
  /** Card title – e.g. ‘Total Users’ */
  title: string
  /** Big central number or string */
  value: number | string
  /** BS Icon class – e.g. 'bi bi-people' */
  iconClass: string
  /** Ribbon + progress bar colour, without the leading dot – e.g. 'primary' */
  theme: 'primary' | 'info' | 'success' | 'warning' | 'danger'
  /** Optional trio of smaller figures shown under the number */
  secondary?: {
    label: string
    value: number | string
    colorClass: string /* any Bootstrap text-* class, e.g. 'text-success' */
  }[]
  /** 0-100 progress bar percentage (omit for none) */
  progressPct?: number
  /** When data is missing, show a warning banner */
  dataUnavailable?: boolean
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  iconClass,
  theme,
  secondary = [],
  progressPct,
  dataUnavailable,
}) => (
  <Card className='h-100 card-shadow rounded-5 border hover-elevate-up overflow-hidden'>
    <div className='ribbon ribbon-top ribbon-vertical'>
      <div className={`ribbon-label bg-${theme}`}>
        <i className={`${iconClass} fs-2 text-white`} />
      </div>
    </div>

    <CardBody className='p-5'>
      <div className='d-flex flex-column'>
        <div className='fw-bold text-muted fs-5 mb-2'>{title}</div>

        {/* main figure */}
        <div className='d-flex align-items-center'>
          <div className='fw-bolder fs-1 text-dark me-2'>{value}</div>
        </div>

        {/* optional mini figures */}
        {secondary.length > 0 && (
          <div className='d-flex align-items-center justify-content-between mt-3'>
            {secondary.map(({label, value, colorClass}) => (
              <div className='d-flex flex-column' key={label}>
                <span className={`fw-semibold fs-7 ${colorClass}`}>{label}</span>
                <span className='fw-bold fs-6'>{value}</span>
              </div>
            ))}
          </div>
        )}

        {/* optional missing-data banner */}
        {dataUnavailable && (
          <div className='bg-light-warning p-3 rounded mt-3 text-center'>
            <span className='text-warning fw-semibold'>Data unavailable</span>
          </div>
        )}

        {/* optional progress bar */}
        {progressPct !== undefined && !dataUnavailable && (
          <div className='bg-light w-100 h-7px rounded mt-3'>
            <div
              className={`bg-${theme} h-7px rounded`}
              style={{width: `${Math.min(progressPct, 100)}%`}}
            />
          </div>
        )}
      </div>
    </CardBody>
  </Card>
)

export default StatCard
